import styled, { css } from 'styled-components';
import { colors, media, remCalc, layout } from '@prisjakt/ui-base';
import CampaignGroup from './components/campaign-group';

export const flexStyle = css`
  min-width: 0;
  flex: 1 1 auto;

  ${media.largeUp`
    flex: 1 1 0%;
  `};
`;

export const gutterStyle = css`
  padding-left: ${layout.spacing.xSmall};
  padding-right: ${layout.spacing.xSmall};
`;

export const dividerStyle = css`
  margin-top: ${layout.margins.component.desktop.rem};

  ${media.mediumDown`
      margin-top: ${layout.margins.component.mobile.rem};
  `};
`;

export const GutterWrapper = styled.div`
  ${flexStyle}
  ${gutterStyle}
`;

export const Campaign = styled.section.attrs({
  'data-test': 'CampaignSection',
})`
  background: ${colors.white};

  /* Safari bug: REM values from the margins in breadcrumb component is causing the
   flip clock animation to break in Safari. Override with px values to fix. */
  .pj-ui-breadcrumb {
    margin: 5px 10px;
  }
`;

export const ProductWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;

  ${media.largeUp`
    padding-top: 10px;
    border-top: 1px solid ${colors.lightGray2};
  `};
`;

export const FiltersWrapper = styled.section`
  ${flexStyle}
  max-width: 220px;
  padding-right: ${remCalc(11)};

  ${media.mediumDown`
    min-width: 100%;
    display: none;

    ${props =>
      props.show &&
      css`
        display: block;
      `}
  `};
`;

export const PromoBanner = styled.div`
  ${dividerStyle}
  display: none;
  text-align: center;
  margin: 0px;
  flex: 1 1 100%;

  ${media.mediumDown`
    display: block;
  `};
`;

export const Filters = styled.div`
  h6 {
    font-size: 14px;
    font-weight: 600;
    padding: 0;
    margin: 0;
    margin-bottom: 10px;
  }

  ${PromoBanner} {
    display: block;
    margin-top: 40px;
  }

  ${media.mediumDown`
    display: none;
    position: fixed;
    overflow: scroll;
    top: 0;
    bottom: 0;
    background: white;
    left: 0;
    right: 0;
    z-index: 100;
    padding: 0;

    h2,
    ${PromoBanner} {
      display: none;
    }

    ${props =>
      props.show &&
      css`
        display: block;
      `}
  `};
`;

export const Filter = styled.div`
  padding-bottom: 10px;
  margin-bottom: 10px;
  border-bottom: 1px solid ${p => p.theme.lines.secondary};
  &:last-child {
    border-bottom: 0;
  }

  ${media.mediumDown`
    margin: 0;
    padding-top: 40px;
    padding-bottom: 60px;
    border-bottom: none;
  `};

  .pj-ui-filter-list--label-text {
    font-size: 12px;
    line-height: 1.2;
  }
`;

export const FilterContent = styled.div`
  ${media.mediumDown`
    margin-top: 60px;
    padding-left: ${layout.margins.wrapper.mobile.rem};
    padding-right: ${layout.margins.wrapper.mobile.rem};
  `};
`;

export const FiltersHeader = styled.div`
  display: none;
  h4 {
    text-align: left;
    padding: 0 ${layout.margins.wrapper.mobile.rem};
    margin-bottom: 0;
    height: 40px;
    line-height: 40px;
    flex: auto;
    button {
      height: 40px;
      width: 40px;
      float: right;
      cursor: pointer;
    }
  }

  ${media.mediumDown`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-around;
    border-bottom: 1px solid ${p => p.theme.lines.primary};
    position: fixed;
    z-index: 10; /* To cover UI elements with abs position (ex. range slider) */
    top: 0;
    left: 0;
    width: 100%;
    background-color: ${p => p.theme.bg.primary};
  `};
`;

export const FiltersButton = styled.div`
  display: none;

  ${media.mediumDown`
    display: flex;
    z-index: 10; /* To cover UI elements with abs position (ex. range slider) */
    width: 100%;
    padding: ${layout.margins.component.mobile.rem}

    button {
      flex: auto;
    }
  `};
`;

export const FiltersPopular = styled.div`
  display: block;
  padding: ${layout.margins.wrapper.desktop.rem};
  margin-top: ${layout.margins.component.desktop.rem};
  margin-bottom: ${layout.margins.component.desktop.rem};
  background-color: #f6f6f6;

  a {
    display: block;
    margin-right: 20px;
    margin-bottom: 7px;
    text-decoration: underline;
  }

  ${media.mediumDown`
    padding: ${layout.margins.wrapper.mobile.rem};
    margin-top: ${layout.margins.component.mobile.rem};
    margin-bottom: ${layout.margins.component.mobile.rem};

    h6 {
      text-align: left;
      font-size: 1rem;
    }
    a {
      display: inline-block;
      margin-right: 15px;
      margin-bottom: 2px;
    }
  `};
`;

export const FilterRanges = styled.div`
  ${dividerStyle}
`;

export const FilterShowButton = styled.div`
  display: none;
  width: 100%;

  ${media.mediumDown`
    display: block;
  `};
`;

export const Search = styled.div`
  ${dividerStyle}
  display: flex;

  ${props =>
    props.hideDesktop &&
    css`
      display: none;
    `}

  ${media.mediumDown`
    margin-bottom: 10px;
    display: flex;

    ${props =>
      props.hideMobile &&
      css`
        display: none;
      `}
  `};
`;

export const Deals = styled.div`
  .pj-ui-panel--grid {
    justify-content: flex-start;
    .pj-ui-product-list-item--grid {
      padding: 20px 10px;
      border-bottom: 1px solid ${p => p.theme.lines.primary};
      flex-grow: 1;
      &.empty {
        flex-grow: 0;
      }
    }
  }
`;

export const Tags = styled.div`
  display: block;
  width: 100%;
  margin-top: 10px;

  ${props =>
    props.hideDesktop &&
    css`
      display: none;
    `}

  ${media.mediumDown`
      display: block;
      margin: 0;
      padding-bottom: 10px;

      &:empty{
        display: none;
      }

      ${props =>
        props.hideMobile &&
        css`
          display: none;
        `}
    `}
`;

export const Products = styled.section`
  ${flexStyle}
`;

export const CampaignGrouping = styled(CampaignGroup)`
  ${flexStyle}
  ${gutterStyle}
`;
